<template>
  <div class="p-8 bg-[#F4F7F9] min-h-screen">
    <header class="flex lg:flex-row justify-between items-start lg:items-center mb-10 gap-6">
      <div>
        <h1 class="text-3xl font-extrabold text-[#222222] tracking-tight">Affichage TV & Dynamic Render</h1>
        <p class="text-gray-500 font-medium">Configurez et personnalisez vos flux de diffusion en temps réel.</p>
      </div>
      <div class="flex gap-3">
        <button class="px-5 py-3 bg-white border border-gray-200 rounded-xl font-bold text-sm shadow-sm hover:bg-gray-50 transition">
          + Ajouter un écran
        </button>
      </div>
    </header>

    <div class="grid  xl:grid-cols-12 gap-8">
      
      <div class="xl:col-span-7 space-y-6">
        <div class="flex items-center justify-between mb-4 px-2">
          <h2 class="text-xs font-black uppercase tracking-[0.2em] text-gray-400">Écrans Connectés ({{ ecrans.length }})</h2>
        </div>

        <div v-for="ecran in ecrans" :key="ecran.id" 
             class="bg-white rounded-[2.5rem] p-6 border border-gray-100 shadow-sm hover:border-dimotec-orange transition-all group"
             :class="{ 'ring-2 ring-dimotec-orange': selectedEcran === ecran.id }"
             @click="selectedEcran = ecran.id">
          
          <div class="flex flex-col md:flex-row gap-6">
            <div class="w-full md:w-48 aspect-video bg-gray-900 rounded-2xl overflow-hidden relative flex items-center justify-center border-4 border-gray-800 shadow-inner">
               <div class="text-center">
                 <p class="text-[8px] font-bold text-dimotec-orange uppercase mb-1">Live</p>
                 <p class="text-white text-xl font-black">{{ ecran.lastNumber }}</p>
               </div>
               <div class="absolute top-2 right-2 flex gap-1">
                 <span class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
               </div>
            </div>

            <div class="flex-1 space-y-4">
              <div class="flex justify-between items-start">
                <div>
                  <h3 class="font-bold text-lg text-gray-900">{{ ecran.nom }}</h3>
                  <p class="text-xs text-gray-400 font-mono">{{ ecran.ip }} • ID: {{ ecran.uid }}</p>
                </div>
                <button class="p-2 bg-gray-50 rounded-lg hover:bg-red-50 hover:text-red-500 transition-colors">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div class="space-y-1">
                  <label class="text-[10px] font-black text-gray-400 uppercase tracking-widest">File Associée</label>
                  <select v-model="ecran.fileId" class="w-full bg-gray-50 border-none rounded-xl py-2 text-xs font-bold focus:ring-2 focus:ring-dimotec-orange/20">
                    <option v-for="f in files" :key="f.id" :value="f.id">{{ f.nom }}</option>
                  </select>
                </div>
                <div class="space-y-1">
                  <label class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Borne Source</label>
                  <select v-model="ecran.borneId" class="w-full bg-gray-50 border-none rounded-xl py-2 text-xs font-bold focus:ring-2 focus:ring-dimotec-orange/20">
                    <option v-for="b in bornes" :key="b.id" :value="b.id">{{ b.nom }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="xl:col-span-5 space-y-6">
        <div class="bg-[#222222] rounded-[3rem] p-8 text-white shadow-2xl sticky top-8 border border-white/5">
          <h2 class="text-xl font-black mb-8 flex items-center gap-3">
            <span class="p-2 bg-blue-500 rounded-xl">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg>
            </span>
            Studio de Design
          </h2>

          <div class="space-y-8">
            <div class="space-y-4">
              <p class="text-xs font-black uppercase tracking-[0.2em] text-gray-500">Identité Visuelle</p>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-[10px] text-gray-400 block mb-2">Couleur Primaire</label>
                  <div class="flex items-center gap-3 bg-white/5 p-2 rounded-xl border border-white/10">
                    <input type="color" v-model="design.primary" class="bg-transparent border-none w-8 h-8 cursor-pointer">
                    <span class="text-xs font-mono">{{ design.primary }}</span>
                  </div>
                </div>
                <div>
                  <label class="text-[10px] text-gray-400 block mb-2">Couleur Fond</label>
                  <div class="flex items-center gap-3 bg-white/5 p-2 rounded-xl border border-white/10">
                    <input type="color" v-model="design.bg" class="bg-transparent border-none w-8 h-8 cursor-pointer">
                    <span class="text-xs font-mono">{{ design.bg }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="space-y-4">
              <p class="text-xs font-black uppercase tracking-[0.2em] text-gray-500">Typographie & Logo</p>
              <select v-model="design.font" class="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-sm font-bold outline-none focus:border-dimotec-orange">
                <option>Plus Jakarta Sans (Moderne)</option>
                <option>Inter (Minimaliste)</option>
                <option>Montserrat (Bold)</option>
              </select>
              
              <div class="border-2 border-dashed border-white/10 rounded-2xl p-6 text-center group hover:border-dimotec-orange transition-colors cursor-pointer">
                <svg class="w-8 h-8 text-gray-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12" /></svg>
                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Remplacer le Logo TV</p>
              </div>
            </div>

            <div class="pt-6 border-t border-white/5">
               <button class="w-full bg-blue-500 py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:scale-[1.02] transition-transform active:scale-95 shadow-xl shadow-blue-900/20">
                 Appliquer à tous les écrans
               </button>
               <p class="text-center text-[10px] text-gray-500 mt-4 italic">La mise à jour prendra effet en 2s via WebSocket.</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedEcran: 1,
      design: {
        primary: '#ed891a',
        bg: '#222222',
        font: 'Plus Jakarta Sans (Moderne)'
      },
      files: [
        { id: 1, nom: "Accueil Principal" },
        { id: 2, nom: "Service SAV" },
        { id: 3, nom: "Retrait Commande" }
      ],
      bornes: [
        { id: 1, nom: "Borne Entrée A" },
        { id: 2, nom: "Borne Entrée B" }
      ],
      ecrans: [
        { id: 1, nom: "TV Murale - Zone Attente", ip: "192.168.1.45", uid: "TV-9920", lastNumber: "A-42", fileId: 1, borneId: 1 },
        { id: 2, nom: "Ecran Vitrine", ip: "192.168.1.46", uid: "TV-9921", lastNumber: "S-12", fileId: 2, borneId: 1 },
        { id: 3, nom: "Affichage Caisse 1", ip: "192.168.1.48", uid: "TV-9924", lastNumber: "R-08", fileId: 3, borneId: 2 }
      ]
    }
  }
}
</script>